import { QueryKey } from "@/api";
import { apiThreadPool } from "@/cache/SpatioTemporalTileCache/ApiQueryThreadPool";
import { unsafeGetColormapByName } from "@/utility/unsafeGetColormapByName";
import type { ColormapCreate } from "@mm/metx-workbench.meteomatics.com";
import { useQuery } from "@tanstack/react-query";

type UseBaseColormapOptions = {
  enabled?: boolean;
  refetchOnMount?: boolean | "always";
  onSuccess?: (colormap: ColormapCreate) => void;
  onError?: (error: Error) => void;
};

const IDX_TRANSPARENT_FLOOR_OFFSET = 0.001;
const IDX_TRANSPARENT_COLOR = "rgba(0, 0, 0, 0)";

const isTransparentColor = (color: string): boolean => color.replace(/\s+/g, "").toLowerCase() === "rgba(0,0,0,0)";

const addIdxTransparentFloorStop = (stops: [number, string][], isIdx: boolean): [number, string][] => {
  if (!isIdx || stops.length === 0) {
    return stops;
  }

  const [lowestValue, lowestColor] = stops[0] ?? [];
  const nextValue = stops[1]?.[0];
  if (!Number.isFinite(lowestValue)) {
    return stops;
  }

  if (
    typeof lowestColor === "string" &&
    isTransparentColor(lowestColor) &&
    Number.isFinite(nextValue) &&
    Math.abs(nextValue - lowestValue - IDX_TRANSPARENT_FLOOR_OFFSET) < 1e-9
  ) {
    return stops;
  }

  const transparentFloorValue = Number((lowestValue - IDX_TRANSPARENT_FLOOR_OFFSET).toFixed(12));
  return [[transparentFloorValue, IDX_TRANSPARENT_COLOR], ...stops];
};

export const useBaseColormap = (currentColormap: string, parameter: string, options?: UseBaseColormapOptions) => {
  return useQuery<ColormapCreate, Error>(
    [QueryKey.COLORMAP_STEPS, currentColormap, parameter],
    async () => {
      const style = unsafeGetColormapByName(currentColormap, parameter as any);
      const response = await apiThreadPool.getColorMapSteps({
        style,
        parameter,
        ensSelect: "",
        calibrated: false,
        vertical_interpolation: "none",
      });

      const stops = addIdxTransparentFloorStop(response.stops, response.exactValues);

      return {
        type: response.segmented ? "segmented" : "continuous",
        label: currentColormap ? `${currentColormap}_custom` : `${String(style)}_custom`,
        stops,
      };
    },
    {
      staleTime: Number.POSITIVE_INFINITY,
      enabled: options?.enabled ?? true,
      refetchOnMount: options?.refetchOnMount,
      onSuccess: options?.onSuccess,
      onError: options?.onError,
    },
  );
};
